// src/pages/Login2.jsx

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import loginBanner from "../assets/banner/loginBanner.png";
import * as Yup from "yup";
import * as UserServices from "../Services/UserServices";
import * as Alert from "../components/common/Alert";
import { loginUser, logoutUser } from "../redux/actions/loginUser";
import { fetchUserProfile } from "../redux/actions/profileAction";
import OTPInput from "../components/input/OTPInput";

const Login2 = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Redux auth state
  const { loading, error, isAuthenticated } = useSelector((s) => s.auth);

  // Local OTP state
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const OTP_RESEND_TIMER = 15;

  // Redirect on successful login
  useEffect(() => {
    let token = localStorage.getItem("access_token");
    let refreshToken = localStorage.getItem("refresh_token");
    if (isAuthenticated && token && refreshToken) {
      navigate("/home");
    } else if (!isAuthenticated && token && refreshToken) {
      dispatch(logoutUser());
    }
  }, [isAuthenticated]);

  // OTP countdown effect
  useEffect(() => {
    if (otpTimer <= 0) return;
    const timer = setInterval(() => setOtpTimer((t) => t - 1), 1000);
    return () => clearInterval(timer);
  }, [otpTimer]);

  const formik = useFormik({
    initialValues: { phone_number: "", otp: "" },
    validationSchema: Yup.object().shape({
      phone_number: Yup.string()
        .matches(/^\d{10}$/, "Phone Number must be 10 digits")
        .required("Phone Number is required"),
      otp: isPhoneVerified
        ? Yup.string()
            .required("OTP is required")
            .matches(/^\d{4,6}$/, "OTP must be numeric and 4-6 digits")
        : Yup.string(),
    }),
    onSubmit: async (values) => {
      if (!isPhoneVerified) {
        try {
          // 1) SEND OTP
          const resp = await UserServices.sendOtpForLogin({
            phone_number: values.phone_number,
          });

          // NOTE: resp here has { data, status, error: null } for 2xx–4xx if you used validateStatus,
          //       or raw axios response { data, status } if not.

          if (resp.status === 200) {
            // OTP sent
            setIsPhoneVerified(true);
            setOtpTimer(OTP_RESEND_TIMER);
            Alert.Alert.SuccessAlert("OTP sent successfully");
          } else {
            // any other non‐403, non‐200
            Alert.Alert.ErrorAlert(resp.data?.message || "Failed to send OTP");
          }
        } catch (err) {
          // 2) ANY NETWORK / UNEXPECTED ERROR

          // Normalize where the status code lives:
          const statusCode = err.status ?? err.response?.status;

          if (statusCode === 403) {
            // KYC‐related 403
            const kycStatus = err.data?.data?.[0]?.status;

            switch (kycStatus) {
              case "Pending":
                Alert.Alert.ErrorAlert(
                  "Your KYC is under review. Please wait while we verify your details."
                );
                navigate("/login");
                break;
              case "KYCNotUploaded":
                Alert.Alert.ErrorAlert(
                  "KYC documents missing! Please upload them to continue."
                );
                navigate("/vendor-kyc", {
                  state: {
                    mobile_no: err.data?.data[0].mobile_no,
                    vendor_id: err.data?.data[0].vendor_id,
                  },
                });
                break;
              case "Rejected":
                Alert.Alert.ErrorAlert(
                  "KYC Rejected. Kindly re-upload valid documents for verification."
                );
                navigate("/vendor-kyc", {
                  state: {
                    mobile_no: err.data?.data[0].mobile_no,
                    vendor_id: err.data?.data[0].vendor_id,
                  },
                });
                break;
              case "Inactive":
                Alert.Alert.ErrorAlert(
                  "Your account is inactive. Please contact support for assistance."
                );
                navigate("/", { state: { mobile_no: values.phone_number } });
                break;
              default:
                Alert.Alert.ErrorAlert(
                  err.data?.message || "Vendor KYC required"
                );
                navigate("/vendor-kyc", {
                  state: { mobile_no: values.phone_number },
                });
            }
          } else {
            // fallback for true exceptions or 5xx
            Alert.Alert.ErrorAlert(
              "Failed to send OTP. Please try again later."
            );
          }
        }
      } else {
        // 3) PHONE VERIFIED → LOG IN
        dispatch(
          loginUser(
            { phone_number: values.phone_number, otp: values.otp },
            navigate
          )
        );
      }
    },
  });

  const handleOTPComplete = (otp) => {
    formik.setFieldValue("otp", otp);
  };

  const handleResendOtp = () => {
    if (otpTimer !== 0) return;

    UserServices.sendOtpForLogin({ phone_number: formik.values.phone_number })
      .then((response) => {
        if (response.status === 200) {
          setOtpTimer(OTP_RESEND_TIMER);
          Alert.Alert.SuccessAlert("OTP resent successfully");
        } else if (response.status === 403) {
          navigate("/vendor-kyc", {
            state: { mobile_no: formik.values.phone_number },
          });
          Alert.Alert.ErrorAlert("You need to complete your KYC to proceed");
        } else {
          Alert.Alert.ErrorAlert("Failed to resend OTP");
        }
      })
      .catch((error) => {
        Alert.Alert.ErrorAlert("Failed to resend OTP");
      });
  };

  return (
    <div className="flex flex-col lg:flex-row">
      {/* Left banner */}
      <div className="w-100 lg:w-1/2 flex justify-center">
        <img
          loading="lazy"
          src={loginBanner}
          alt="Login Banner"
          className="h-[88vh] lg:w-[50vw]"
        />
      </div>

      {/* Right form */}
      <div className="w-100 lg:w-1/2 flex justify-center md:justify-start items-center lg:h-[88vh] overflow-y-auto">
        <div className="w-100 px-[5vh] md:px-[10vh] lg:px-[15vh] py-5">
          <h6 className="fw-bold text-xl md:text-2xl lg:text-4xl">Login</h6>
          <p className="text-gray-500 mt-1">Let’s Start with Log in</p>

          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

          <form onSubmit={formik.handleSubmit} className="xl:pr-5 mt-5">
            {/* Phone */}
            <div>
              <label className="text-lg lg:text-xl">Phone Number</label>
              <input
                type="text"
                name="phone_number"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.phone_number}
                className="form-control mt-2"
                placeholder="Enter Mobile Number"
              />
              {formik.touched.phone_number && formik.errors.phone_number && (
                <p className="text-danger text-sm px-2">
                  {formik.errors.phone_number}
                </p>
              )}
            </div>

            {/* OTP & Resend */}
            {isPhoneVerified && (
              <div className="mt-3">
                <label className="text-lg lg:text-xl">OTP</label>
                <OTPInput length={4} onComplete={handleOTPComplete} />
                {formik.touched.otp && formik.errors.otp && (
                  <p className="text-danger text-sm px-2">
                    {formik.errors.otp}
                  </p>
                )}
                <div className="mt-2 flex justify-between">
                  {otpTimer > 0 ? (
                    <span className="text-sm text-gray-500">
                      Resend OTP in {otpTimer}s
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      className="btn btn-link text-sm p-0"
                    >
                      Resend OTP
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Submit */}
            <div className="mt-5">
              <button
                type="submit"
                className="btn theme-outline-btn w-100"
                disabled={loading}
              >
                {loading
                  ? "Processing..."
                  : isPhoneVerified
                  ? "Login"
                  : "Send OTP"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login2;
